
import { useState, useEffect } from 'react';
import { FormData } from '@/types/onboarding';

export const useOnboardingState = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<'forward' | 'backward'>('forward');
  const [cardTransitionState, setCardTransitionState] = useState<'active' | 'exiting' | 'entering'>('active');
  const [showConfetti, setShowConfetti] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    email: '',
    motivation: '',
    experience: '',
    challenge: '',
    useCase: '',
    interviewInterest: ''
  });

  // Auto-save to localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('show-up-onboarding');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Ensure all required fields exist in the parsed data
        setFormData({
          firstName: parsed.firstName || '',
          email: parsed.email || '',
          motivation: parsed.motivation || '',
          experience: parsed.experience || '',
          challenge: parsed.challenge || '',
          useCase: parsed.useCase || '',
          interviewInterest: parsed.interviewInterest || '',
          motivationOther: parsed.motivationOther,
          currentHabitApp: parsed.currentHabitApp,
          useCaseOther: parsed.useCaseOther
        });
      } catch (error) {
        console.log('Error loading saved data:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('show-up-onboarding', JSON.stringify(formData));
  }, [formData]);

  // Trigger confetti when reaching thank you page
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(() => {
        setShowConfetti(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isComplete]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const canProceed = () => {
    if (currentStep === 0) {
      return formData.firstName.trim().length > 0 && formData.email.trim().length > 0 && formData.email.includes('@');
    }
    if (currentStep === 1) {
      const hasMotivation = formData.motivation.length > 0;
      if (formData.motivation === "Other comments") {
        return hasMotivation && formData.motivationOther && formData.motivationOther.trim().length > 0;
      }
      return hasMotivation;
    }
    if (currentStep === 2) {
      const hasExperience = formData.experience.length > 0;
      if (formData.experience === "I'm currently using a habit app") {
        return hasExperience && formData.currentHabitApp && formData.currentHabitApp.trim().length > 0;
      }
      return hasExperience;
    }
    if (currentStep === 3) return formData.challenge.length > 0;
    if (currentStep === 4) {
      const hasUseCase = formData.useCase.length > 0;
      if (formData.useCase === "Others") {
        return hasUseCase && formData.useCaseOther && formData.useCaseOther.trim().length > 0;
      }
      return hasUseCase;
    }
    if (currentStep === 5) return formData.interviewInterest && formData.interviewInterest.length > 0;
    return false;
  };

  const executeStepTransition = (direction: 'forward' | 'backward', callback: () => void) => {
    setIsTransitioning(true);
    setTransitionDirection(direction);
    setCardTransitionState('exiting');

    // Phase 1: Card slides out
    setTimeout(() => {
      callback();
      setCardTransitionState('entering');
    }, 200);

    // Phase 2: New card slides in
    setTimeout(() => {
      setCardTransitionState('active');
      setIsTransitioning(false);
    }, 400);
  };

  return {
    currentStep,
    setCurrentStep,
    isComplete,
    setIsComplete,
    isTransitioning,
    transitionDirection,
    cardTransitionState,
    showConfetti,
    formData,
    handleInputChange,
    canProceed,
    executeStepTransition
  };
};
